import { TextField, useTheme } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import * as React from "react";

import { RobotoButton, RobotoLoadingButton } from "@/shared/components";
import { useCreateDirectoryMutation } from "@/shared/domain/files/hooks";
import { useFileBrowserParentPath } from "@/shared/hooks";

interface NewDirectoryDialogProps {
  open: boolean;
  datasetId: string;
  onClose: () => void;
}

const UNTITLED_FOLDER_NAME = "Untitled folder";

export const NewDirectoryDialog: React.FC<NewDirectoryDialogProps> = ({
  datasetId,
  open,
  onClose,
}) => {
  const theme = useTheme();

  const createDirectoryMutation = useCreateDirectoryMutation();

  const [directoryName, setDirectoryName] =
    React.useState(UNTITLED_FOLDER_NAME);
  const [error, setError] = React.useState<string | null>(null);

  // autofocus doesn't work inside Dialog. Use a ref and setTimeout
  const inputRef = React.useRef<null | HTMLInputElement>(null);

  const parentPath = useFileBrowserParentPath();

  React.useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [open]);

  const handleClose = () => {
    onClose();
    setDirectoryName(UNTITLED_FOLDER_NAME);
    setError(null);
  };

  const createDirectory = () => {
    setError(null);
    createDirectoryMutation
      .mutateAsync({
        datasetId,
        directoryName,
        parentPath: parentPath ?? null,
      })
      .then(handleClose)
      .catch((error) => {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError("Failed to create folder. Please try again");
        }
      });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    createDirectory();
  };

  return (
    /* disableRestoreFocus stops the parent view button from being focused on enter press */
    <Dialog open={open} onClose={onClose} disableRestoreFocus>
      <DialogTitle sx={{ paddingLeft: theme.spacing(2) }}>
        New folder
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <TextField
          placeholder={UNTITLED_FOLDER_NAME}
          variant={"outlined"}
          size={"small"}
          sx={{
            minWidth: "300px",
            marginLeft: theme.spacing(2),
            marginRight: theme.spacing(2),
          }}
          value={directoryName}
          onChange={(e) => setDirectoryName(e.target.value)}
          inputRef={inputRef}
          error={!!error}
          helperText={error}
        />
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            margin: theme.spacing(2),
          }}
        >
          <RobotoButton
            variant={"text"}
            onClick={handleClose}
            eventName={"CreateNewFolderCanceled"}
            eventProperties={{
              datasetId,
            }}
          >
            Cancel
          </RobotoButton>
          <RobotoLoadingButton
            variant="contained"
            loading={createDirectoryMutation.isPending}
            sx={{ ml: theme.spacing(1) }}
            eventName={"CreateNewFolderSubmitted"}
            eventProperties={{
              datasetId,
            }}
            onClick={createDirectory}
          >
            Create
          </RobotoLoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
